/**
 * Toast — Tri-mode notification.
 *
 * Undo mode (onUndo provided):    5s duration, teal progress bar, "Undo" button.
 * Action mode (onAction provided): 2.2s duration, no progress bar, labelled button.
 * Brief mode (no button):          1.8s duration, no progress bar, no button.
 *
 * Positioned above bottom nav: `bottom: 72px`.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { T } from '@/styles/tokens';

interface ToastProps {
  message: string;
  isVisible: boolean;
  onDismiss: () => void;
  onUndo?: () => void;
  onAction?: () => void;
  actionLabel?: string;
}

export function Toast({ message, isVisible, onDismiss, onUndo, onAction, actionLabel }: ToastProps) {
  const hasUndo = !!onUndo;
  const hasAction = !!onAction && !!actionLabel;
  const duration = hasUndo ? 5000 : hasAction ? 3200 : 1800;
  const [progress, setProgress] = useState(100);
  const startRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isVisible) {
      cleanup();
      setProgress(100);
      return;
    }

    startRef.current = Date.now();
    setProgress(100);

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(pct);
      if (pct <= 0) {
        cleanup();
        setTimeout(onDismiss, 150);
      }
    }, 50);

    return cleanup;
  }, [isVisible, duration, onDismiss, cleanup]);

  const handleUndo = useCallback(() => {
    cleanup();
    onUndo?.();
    onDismiss();
  }, [onUndo, onDismiss, cleanup]);

  const handleAction = useCallback(() => {
    cleanup();
    onAction?.();
    onDismiss();
  }, [onAction, onDismiss, cleanup]);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 70px + 12px)',
        left: 12,
        right: 12,
        zIndex: 90,
        maxWidth: 396,
        margin: '0 auto',
        background: '#2D2D2D',
        borderRadius: 10,
        padding: '13px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
        transform: `translateY(${isVisible ? '0' : '12px'})`,
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        transition: 'opacity 200ms ease, transform 200ms ease',
        overflow: 'hidden',
        fontFamily: T.fontFamily,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" fill={T.primary} opacity="0.2" />
          <path d="M8 12.5l2.5 2.5 5.5-5.5" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span style={{ fontSize: 14, color: '#FFFFFF', fontWeight: 500 }}>
          {message}
        </span>
      </div>

      {hasUndo && (
        <button
          onClick={handleUndo}
          style={{
            background: 'transparent',
            border: 'none',
            color: T.primary,
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            padding: `2px ${T.sm}`,
            fontFamily: T.fontFamily,
            flexShrink: 0,
          }}
        >
          Undo
        </button>
      )}

      {hasAction && !hasUndo && (
        <button
          onClick={handleAction}
          style={{
            background: 'transparent',
            border: 'none',
            color: T.primary,
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            padding: `2px ${T.sm}`,
            fontFamily: T.fontFamily,
            flexShrink: 0,
          }}
        >
          {actionLabel}
        </button>
      )}

      {/* Undo progress bar — teal, 3px, bottom edge */}
      {hasUndo && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: 3,
          width: `${progress}%`,
          background: T.primary,
          transition: 'width 50ms linear',
        }} />
      )}
    </div>
  );
}

export default Toast;
